import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ContentService } from '../../../../core/services/content.service';
import { REGION_CODES } from '../../../../core/constants/referentials';
import { UpcomingEvent, UpcomingEventFilters } from '../../../../core/models';
import {
  eventCategoryLabel,
  eventCategoryLabelKey,
  eventCityLabel,
  eventTypeLabel,
  eventTypeLabelKey,
} from '../../../../shared/utils/upcoming-event.mapper';
import { EventCard } from '../../../../shared/ui/cards/event-card/event-card';
import { pollSilently } from '../../../../shared/utils/silent-polling';
import { ScrollReveal } from '../../../../shared/directives/scroll-reveal';

const PAGE_SIZE = 9;

@Component({
  selector: 'app-events-list',
  imports: [EventCard, ScrollReveal, TranslatePipe],
  templateUrl: './events-list.html',
})
export class EventsList {
  private readonly router = inject(Router);
  private readonly contentService = inject(ContentService);
  private readonly translate = inject(TranslateService);

  readonly cityLabel = eventCityLabel;

  /** Libellé traduit d'un type d'événement pour les <option> de filtre (repli sur le libellé brut si le type est inconnu). */
  eventTypeLabel(type: string): string {
    const key = eventTypeLabelKey(type);
    return key ? this.translate.instant(key) : eventTypeLabel(type);
  }

  /** Libellé traduit d'une catégorie pour les <option> de filtre (repli sur le libellé brut si la catégorie est inconnue). */
  categoryLabel(category: string): string {
    const key = eventCategoryLabelKey(category);
    return key ? this.translate.instant(key) : eventCategoryLabel(category);
  }

  readonly query = signal('');
  readonly regionFilter = signal('');
  readonly cityFilter = signal('');
  readonly eventTypeFilter = signal('');
  readonly categoryFilter = signal('');
  readonly page = signal(0);

  readonly loading = signal(true);
  readonly error = signal(false);
  readonly events = signal<UpcomingEvent[]>([]);
  readonly totalElements = signal(0);
  readonly totalPages = signal(1);

  readonly availableFilters = signal<UpcomingEventFilters | null>(null);
  /** Codes région (referentials.ts) → clé i18n `regions.*`. */
  readonly regionOptions = computed(() => {
    const codes = this.availableFilters()?.regions;
    const allCodes = Object.values(REGION_CODES);
    return codes ? allCodes.filter((code) => codes.includes(code)) : allCodes;
  });
  readonly cityOptions = computed(() => this.availableFilters()?.cities ?? []);
  readonly eventTypeOptions = computed(() => this.availableFilters()?.eventTypes ?? []);
  readonly categoryOptions = computed(() => this.availableFilters()?.categories ?? []);

  constructor() {
    this.loadFilters();
    this.search();
    // Rafraîchissement silencieux : les nouveaux événements publiés apparaissent sans que le
    // visiteur ait à recharger la page (mêmes filtres/page courants, pas de spinner).
    pollSilently(() => this.search(true));
  }

  private loadFilters(): void {
    this.contentService.upcomingEventFilters().subscribe({
      next: (filters) => this.availableFilters.set(filters),
      error: () => {
        // Silencieux : les select retombent sur le référentiel complet.
      },
    });
  }

  private search(silent = false): void {
    if (!silent) {
      this.loading.set(true);
      this.error.set(false);
    }
    this.contentService
      .upcomingEvents({
        query: this.query().trim() || undefined,
        regionCode: this.regionFilter() || undefined,
        cityCode: this.cityFilter() || undefined,
        eventType: this.eventTypeFilter() || undefined,
        categoryCode: this.categoryFilter() || undefined,
        page: this.page(),
        size: PAGE_SIZE,
      })
      .subscribe({
        next: (result) => {
          this.events.set(result.content);
          this.totalElements.set(result.totalElements);
          this.totalPages.set(Math.max(1, result.totalPages));
          this.loading.set(false);
        },
        error: () => {
          if (!silent) {
            this.loading.set(false);
            this.error.set(true);
          }
        },
      });
  }

  applyQuery(): void {
    this.page.set(0);
    this.search();
  }

  setRegion(region: string): void {
    this.regionFilter.set(region);
    this.page.set(0);
    this.search();
  }

  setCity(city: string): void {
    this.cityFilter.set(city);
    this.page.set(0);
    this.search();
  }

  setEventType(eventType: string): void {
    this.eventTypeFilter.set(eventType);
    this.page.set(0);
    this.search();
  }

  setCategory(category: string): void {
    this.categoryFilter.set(category);
    this.page.set(0);
    this.search();
  }

  resetFilters(): void {
    this.query.set('');
    this.regionFilter.set('');
    this.cityFilter.set('');
    this.eventTypeFilter.set('');
    this.categoryFilter.set('');
    this.page.set(0);
    this.search();
  }

  previousPage(): void {
    if (this.page() > 0) {
      this.page.update((p) => p - 1);
      this.search();
    }
  }

  nextPage(): void {
    if (this.page() + 1 < this.totalPages()) {
      this.page.update((p) => p + 1);
      this.search();
    }
  }

  openEvent(slug: string): void {
    this.router.navigate(['/events', slug]);
  }
}
